import React, { useEffect, useState } from "react";
import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";
import TypeApi from "../../api/TypeApi";

import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";

function List() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  /* ================= FETCH DATA ================= */
  const fetchTypes = async () => {
    try {
      setLoading(true);
      const res = await TypeApi.list();
      if (res.code === 200) {
        setTypes(res.data);
      } else setError(res.message || "Failed to fetch data");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  /* ================= DELETE LOGIC ================= */
  const handleDelete = async () => {
    try {
      const res = await TypeApi.delete({ id: selectedType._id });
      if (res.code === 200) {
        const modalEl = document.getElementById("delete-type-modal");
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        if (modalInstance) modalInstance.hide();
        fetchTypes();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const filteredTypes = types.filter((item) => {
    const search = searchTerm.toLowerCase().trim();
    return item.type?.toLowerCase().includes(search);
  });

  return (
    <div className="content pb-0">
      {/* ================= HEADER (UI from Commented Code) ================= */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-4 flex-wrap">
        <div>
          <h4 className="mb-1">
            Type Management{" "}
            <span className="badge badge-soft-primary ms-2">
              {filteredTypes.length}
            </span>
          </h4>
        </div>
      </div>

      <div className="gap-2 d-flex align-items-center mb-3 justify-content-between flex-wrap">
        {/* SEARCH FILTER */}
        <div className="group-search-box position-relative">
          <i className="ti ti-search search-icon"></i>

          <input
            type="text"
            className="form-control search-input"
            placeholder="Search by Type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {/* ✅ Logic: Check 'write' permission before showing Add Button */}
        {can(permissions, "write", "type") && (
          <button
            type="button"
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#standard-modal"
          >
            <i className="ti ti-square-rounded-plus-filled me-1" />
            Add Type
          </button>
        )}
      </div>

      {/* ================= TABLE (UI from Commented Code) ================= */}
      <div className="card border-0 rounded-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive custom-table">
            <table className="table table-nowrap align-middle">
              <thead className="table-light">
                <tr>
                  <th>S.no</th>
                  <th>Lead Type Title</th>
                  <th>Created on</th>
                  {/* ✅ Logic: Action column only if user has write/delete access */}
                  {(can(permissions, "write", "type") ||
                    can(permissions, "delete", "type")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="4" className="text-danger text-center">
                      {error}
                    </td>
                  </tr>
                ) : (
                  filteredTypes.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td className="fw-bold">{item.type}</td>
                      <td>
                        {new Date(item.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* ✅ Logic: Edit button check */}
                          {can(permissions, "write", "type") && (
                            <button
                              className="btn btn-soft-success btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#edit-type-modal"
                              onClick={() => setSelectedType(item)}
                            >
                              <i className="ti ti-edit" />
                            </button>
                          )}
                          {/* ✅ Logic: Delete button check */}
                          {can(permissions, "delete", "type") && (
                            <button
                              className="btn btn-soft-danger btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#delete-type-modal"
                              onClick={() => setSelectedType(item)}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= MODALS ================= */}
      <div
        id="standard-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
      >
        <AddDetails onAdded={fetchTypes} />
      </div>

      <div
        id="edit-type-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
      >
        <EditDetails
          typeData={selectedType}
          fetchTypes={fetchTypes}
          clearSelection={() => setSelectedType(null)}
        />
      </div>

      {/* Delete Confirmation Modal (UI from Commented Code) */}
      <div
        className="modal fade"
        id="delete-type-modal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm rounded-0">
          <div className="modal-content rounded-0">
            <div className="modal-body p-4 text-center position-relative">
              <div className="mb-3 position-relative z-1">
                <span className="avatar avatar-xl badge-soft-danger border-0 text-danger rounded-circle">
                  <i className="ti ti-trash fs-24"></i>
                </span>
              </div>
              <h5 className="mb-1">Delete Confirmation</h5>
              <p className="small text-muted mb-3">
                Are you sure you want to remove the selected type?
              </p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-light w-100"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={handleDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
